import React, { useEffect, useMemo, useState } from "react";
import { Skeleton } from "antd";
import ScrollCarousel from "./ScrollCarousel";
import DestinationCard from "./DestinationCard";
import SectionHeader from "./SectionHeader";
import useBreakpoint from "../../../../utils/useBreakpoint";
import type { TravelMode } from "../Homepage";
import LocationsApi from "../../../../api-services/locations-api.service";
import type { Location } from "../../../../interfaces/country.interface";

interface Props {
  mode?: TravelMode;
  locationGroupCodes?: string[];
}

const locationsApi = new LocationsApi();
const DESTINATIONS_LISTING_HREF = "/web/destinations";
const HOMEPAGE_ITEM_LIMIT = 10; // 2 rows of the desktop 5-column grid

export default function ExploreDestinations({
  mode = "tour",
  locationGroupCodes = ["GD_Standard", "GD_In_Depth"],
}: Props) {
  const isCruise = mode === "cruise";
  const isMobile = useBreakpoint();
  const [destinations, setDestinations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const groupKey = useMemo(() => locationGroupCodes.join(","), [locationGroupCodes]);

  useEffect(() => {
    let active = true;

    setLoading(true);
    locationsApi.getLocations({ group: locationGroupCodes, highlighted: true, limit: HOMEPAGE_ITEM_LIMIT })
      .then(({ locations }) => {
        if (active) setDestinations(locations as Location[]);
      })
      .catch(() => {
        if (active) setDestinations([]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [groupKey]);

  if (!loading && destinations.length === 0) return null;

  const cards = destinations.map((destination) => (
    <DestinationCard key={destination.id} destination={destination} />
  ));

  const skeletonCards = Array.from({ length: isMobile ? 3 : 10 }, (_, index) => (
    <div key={index} className="v2-tag-card">
      <Skeleton.Image active />
    </div>
  ));

  const title = isCruise ? (
    <>
      <span className="v2-explore__title-accent">Popular</span> Right Now
    </>
  ) : (
    "Explore More Destinations"
  );

  return (
    <section id="explore-destinations-v2">
      <div className="v2-explore__panel">
        <SectionHeader title={title} subtitle="What other travellers are booking" viewAllHref={DESTINATIONS_LISTING_HREF} />

        {isMobile ? (
          <ScrollCarousel trackClassName="v2-explore__track" step={166}>
            {loading ? skeletonCards : cards}
          </ScrollCarousel>
        ) : (
          <div className="v2-explore__grid">{loading ? skeletonCards : cards}</div>
        )}
      </div>
    </section>
  );
}
